import { useState } from 'react';

export interface Filters {
  categories: string[];
  minRating: number;
  maxDistance: number;
  priceRanges: number[];
  onlyNew: boolean;
  onlyDiscount: boolean;
}

interface FilterSidebarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onReset: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORIES = ['Africain', 'Français', 'Italien', 'Asiatique', 'Fast-food', 'Végétarien'];
const PRICE_LABELS: Record<number, string> = { 1: '€', 2: '€€', 3: '€€€' };

export default function FilterSidebar({ filters, onChange, onReset, isOpen, onClose }: FilterSidebarProps) {
  const [localFilters, setLocalFilters] = useState<Filters>(filters);

  const toggleCategory = (cat: string) => {
    const updated = localFilters.categories.includes(cat)
      ? localFilters.categories.filter(c => c !== cat)
      : [...localFilters.categories, cat];
    const next = { ...localFilters, categories: updated };
    setLocalFilters(next);
    onChange(next);
  };

  const togglePrice = (p: number) => {
    const updated = localFilters.priceRanges.includes(p)
      ? localFilters.priceRanges.filter(x => x !== p)
      : [...localFilters.priceRanges, p];
    const next = { ...localFilters, priceRanges: updated };
    setLocalFilters(next);
    onChange(next);
  };

  const setRating = (r: number) => {
    const next = { ...localFilters, minRating: r };
    setLocalFilters(next);
    onChange(next);
  };

  const setDistance = (d: number) => {
    const next = { ...localFilters, maxDistance: d };
    setLocalFilters(next);
    onChange(next);
  };

  const toggleBool = (key: 'onlyNew' | 'onlyDiscount') => {
    const next = { ...localFilters, [key]: !localFilters[key] };
    setLocalFilters(next);
    onChange(next);
  };

  const handleReset = () => {
    const def: Filters = {
      categories: [],
      minRating: 0,
      maxDistance: 10,
      priceRanges: [],
      onlyNew: false,
      onlyDiscount: false,
    };
    setLocalFilters(def);
    onReset();
  };

  const activeCount =
    localFilters.categories.length +
    localFilters.priceRanges.length +
    (localFilters.minRating > 0 ? 1 : 0) +
    (localFilters.maxDistance < 10 ? 1 : 0) +
    (localFilters.onlyNew ? 1 : 0) +
    (localFilters.onlyDiscount ? 1 : 0);

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-equalizer-2-line text-xl text-teal-600"></i>
          </div>
          <h2 className="text-lg font-bold text-gray-900">Filtres</h2>
          {activeCount > 0 && (
            <span className="bg-teal-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <button
              onClick={handleReset}
              className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
            >
              Réinitialiser
            </button>
          )}
          <button
            onClick={onClose}
            className="lg:hidden w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-7 pr-1">
        {/* Cuisine */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Type de cuisine</h3>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  localFilters.categories.includes(cat)
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Note minimale */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Note minimale</h3>
          <div className="flex gap-2">
            {[0, 3, 3.5, 4, 4.5].map(r => (
              <button
                key={r}
                onClick={() => setRating(r)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer border whitespace-nowrap ${
                  localFilters.minRating === r
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                }`}
              >
                {r === 0 ? (
                  'Tous'
                ) : (
                  <>
                    <i className="ri-star-fill text-xs"></i>
                    {r}+
                  </>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Distance */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">Distance max</h3>
            <span className="text-sm font-bold text-teal-600">
              {localFilters.maxDistance >= 10 ? 'Tous' : `${localFilters.maxDistance} km`}
            </span>
          </div>
          <input
            type="range"
            min={0.5}
            max={10}
            step={0.5}
            value={localFilters.maxDistance}
            onChange={e => setDistance(parseFloat(e.target.value))}
            className="w-full accent-teal-600 cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>0,5 km</span>
            <span>5 km</span>
            <span>10 km</span>
          </div>
        </div>

        {/* Prix */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Gamme de prix</h3>
          <div className="flex gap-2">
            {[1, 2, 3].map(p => (
              <button
                key={p}
                onClick={() => togglePrice(p)}
                className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer border ${
                  localFilters.priceRanges.includes(p)
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                }`}
              >
                {PRICE_LABELS[p]}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-1.5 px-1">
            <span>Économique</span>
            <span>Moyen</span>
            <span>Premium</span>
          </div>
        </div>

        {/* Options */}
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Options</h3>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                onClick={() => toggleBool('onlyNew')}
                className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                  localFilters.onlyNew ? 'bg-teal-600' : 'bg-gray-200'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  localFilters.onlyNew ? 'translate-x-5' : 'translate-x-0.5'
                }`}></div>
              </div>
              <span className="text-sm text-gray-700 group-hover:text-gray-900">Nouveaux restaurants</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer group">
              <div
                onClick={() => toggleBool('onlyDiscount')}
                className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${
                  localFilters.onlyDiscount ? 'bg-teal-600' : 'bg-gray-200'
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                  localFilters.onlyDiscount ? 'translate-x-5' : 'translate-x-0.5'
                }`}></div>
              </div>
              <span className="text-sm text-gray-700 group-hover:text-gray-900">Promotions en cours</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-72 flex-shrink-0">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-28">
          {sidebarContent}
        </div>
      </aside>

      {/* Mobile drawer */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
          <div className="relative ml-auto w-80 max-w-full bg-white h-full shadow-2xl p-6 overflow-y-auto">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}
